import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  IconButton,
  CircularProgress,
  Stack,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  editUamUserdetails,
  getAllUserGroupsByApplication,
  getUamUserdetails
} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';

// ── Types ──
interface PodAssignment {
  application_name: string;
}

export interface UserInfo {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  created_at: string;
  pods: PodAssignment[];
  user_application_name: string;
}

interface Group {
  id: string;
  name: string;
  selected: boolean;
}

interface Pod {
  id: string;
  name: string;
  application_name: string;
  selected: boolean;
  groups: Group[];
}

interface MasterPod {
  application_name: string;
  name: string;
  groups: any[];
}

interface EditPodAccessDialogProps {
  open: boolean;
  onClose: () => void;
  userId: any | null;
}

export const EditPodAccessDialog: React.FC<EditPodAccessDialogProps> = ({
  open,
  onClose,
  userId
}) => {
  const theme = useTheme();
  const [pods, setPods] = useState<Pod[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && userId) {
      const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const payload: any = {id: userId};
          const tk = EncryptUtils.encryptedData(payload);
          const userResponse = await getUamUserdetails({tk});
          const masterPodsResponse = await getAllUserGroupsByApplication();
          const masterPods: MasterPod[] = Array.isArray(
            masterPodsResponse?.data
          )
            ? masterPodsResponse.data
            : [];

          if (userResponse?.success && userResponse?.data) {
            const userData: UserInfo = userResponse.data;
            setUserInfo(userData);

            const assignedGroups = userData.user_application_name
              ? userData.user_application_name.split(',').map(g => g.trim())
              : [];
            const uiPods: Pod[] = masterPods.map((mp, pIndex: number) => {
              const userAssignedGroups = assignedGroups.includes(
                mp.application_name
              );
              const isPodAssignedToUser = !!userAssignedGroups;
              return {
                id: `pod_${pIndex}`,
                name: mp.name,
                application_name: mp.application_name,
                selected: isPodAssignedToUser,
                groups: (mp.groups || []).map((groupName: any) => ({
                  id: groupName.groupid,
                  name: groupName.groupName,
                  selected: isPodAssignedToUser
                    ? userAssignedGroups || []
                    : false
                }))
              };
            });
            setPods(uiPods);
          } else {
            setError('Failed to load user details.');
          }
        } catch (err) {
          setError('Failed to load access data. Please try again.');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    } else if (!open) {
      setPods([]);
      setUserInfo(null);
    }
  }, [open, userId]);

  const handlePodToggle = (podId: string) => {
    setPods(prevPods =>
      prevPods.map(pod => {
        if (pod.id !== podId) return pod;
        const nextSelectedState = !pod.selected;
        return {
          ...pod,
          selected: nextSelectedState,
          groups: pod.groups.map(group => ({
            ...group,
            selected: nextSelectedState
          }))
        };
      })
    );
  };

  const handleGroupToggle = (podId: string, groupId: string) => {
    setPods(prevPods =>
      prevPods.map(pod => {
        if (pod.id === podId) {
          return {
            ...pod,
            groups: pod.groups.map(group =>
              group.id === groupId
                ? {...group, selected: !group.selected}
                : group
            )
          };
        }
        return pod;
      })
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatedPodsPayload: string = pods
        .filter(pod => pod.selected)
        .map(pod => pod.application_name)
        .join(',');
      const tk = EncryptUtils.encryptedData({
        id: userInfo?.id,
        pods: updatedPodsPayload
      });
      await editUamUserdetails({tk});
      setIsSaving(false);
      onClose();
    } catch (err) {
      console.error('error', err);
      setIsSaving(false);
    }
  };

  const selectedPodCount = pods.filter(p => p.selected).length;
  const selectedGroupCount = pods.reduce(
    (sum, p) => sum + p.groups.filter(g => g.selected).length,
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{sx: {borderRadius: 2}}}>
      {/* Header */}
      <DialogTitle sx={{pb: 1}}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Iconify
            icon="solar:pen-bold"
            width={24}
            sx={{color: theme.palette.primary.main}}
          />
          <Box>
            <Typography variant="h6" component="span">
              Edit Pod Access
            </Typography>
            {userInfo && (
              <Typography variant="body2" color="text.secondary">
                {userInfo.full_name} &middot; {userInfo.email}
              </Typography>
            )}
          </Box>
        </Stack>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'text.disabled'
          }}>
          <Iconify icon="mingcute:close-line" width={20} />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{p: 2.5, minHeight: 200}}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200
            }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Select pods and at least one group per pod.
            </Typography>

            <Stack spacing={1.5}>
              {pods.map(pod => (
                <Box
                  key={pod.id}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    p: 2,
                    bgcolor: pod.selected
                      ? alpha(theme.palette.primary.main, 0.04)
                      : 'transparent',
                    transition: 'background-color 0.2s'
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pod.selected}
                        onChange={() => handlePodToggle(pod.id)}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">{pod.name}</Typography>
                        {/* {pod.selected && (
                          <Label variant="soft" color="success" sx={{height: 22}}>
                            {pod.groups.filter((g) => g.selected).length}
                          </Label>
                        )} */}
                      </Stack>
                    }
                    sx={{ml: -0.5, mb: pod.selected ? 1 : 0}}
                  />

                  {pod.selected && (
                    <Box sx={{pl: 4}}>
                      <FormGroup row>
                        {pod.groups.map(group => (
                          <FormControlLabel
                            key={group.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={group.selected}
                                onChange={() =>
                                  handleGroupToggle(pod.id, group.id)
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {group.name}
                              </Typography>
                            }
                            sx={{mr: 3, mb: 0.5}}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <Divider />

      {/* Footer */}
      <DialogActions sx={{p: 2}}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{width: '100%'}}>
          <Typography variant="caption" color="text.disabled">
            {selectedPodCount} pod(s)
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={onClose}
              variant="outlined"
              color="inherit"
              disabled={isLoading || isSaving}>
              Cancel
            </Button>
            <LoadingButton
              onClick={handleSave}
              variant="contained"
              disabled={isLoading || isSaving}
              loading={isSaving}>
              Save Changes
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
